import { Component, OnInit, OnDestroy, HostListener, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SharedModule } from '../../../shared/shared.module';
import { SelectButtonModule } from 'primeng/selectbutton';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { StaffApiService } from '../../../core/api/services';
import { OfflineDataService } from '../../../core/services/offline-data.service';
import { PatientBipInfoModel } from '../../../core/api/models';
import { AlertService } from '../../../shared/services/alert.service';
import { Editor, editorConfig } from '../../../shared/ckeditor/ckeditor-config';
import { tap } from 'rxjs/operators';
import { AuthService } from '../../../auth/auth.service';

@Component({
    selector: 'app-bip-crisis-cycle',
    standalone: true,
    imports: [CommonModule, FormsModule, SharedModule, SelectButtonModule, CKEditorModule],
    templateUrl: './bip-crisis-cycle.component.html',
    styleUrl: '../../supervisor-main.component.css'
})
export class BipCrisisCycleComponent implements OnInit, OnDestroy {
    patientID = input.required<string>();
    loading = signal<boolean>(false);
    currentModel: PatientBipInfoModel | null = null;
    draftModel: PatientBipInfoModel | null = null;
    crisisCycle: string | null = null; // draft content
    initialCrisisCycle: string = '';
    public Editor = Editor;
    public editorConfig = editorConfig;
    isCurrentView: boolean = true;
    useOfflineData = input<boolean>(false);
    showToggle = input<boolean>(true);
    private autoSaveHandle: any;
    canEditBip: boolean = false;

    constructor(private staffApi: StaffApiService, private alerts: AlertService, private offlineDataService: OfflineDataService, private authService: AuthService) { }

    // Normalize optional input to a boolean defaulting to true
    shouldShowToggle(): boolean {
        const value = this.showToggle();
        return (value ?? true) && this.canEditBip;
    }

    ngOnInit(): void {
        this.canEditBip = this.authService.canEditBip();
        if (!this.canEditBip) {
            this.isCurrentView = true;
        }
        const useOffline = this.useOfflineData() || this.offlineDataService.isInOfflineMode();
        if (useOffline) {
            this.loadDataOffline();
        } else {
            this.loadData();
        }

        // Auto-save every 30s when in Draft view and draft model exists
        if (this.canEditBip) {
            this.scheduleAutoSave();
        }
    }

    private scheduleAutoSave(): void {
        this.autoSaveHandle = setTimeout(() => {
            if (!this.isCurrentView && this.draftModel) {
                this.getSaveObservable(false)?.subscribe({ next: () => {}, error: () => {} });
            }
            this.scheduleAutoSave();
        }, 30000);
    }
    loadData() {
        this.loading.set(true);
        if (!this.canEditBip) {
            this.staffApi.GetCurrentPatientBIPInfo({ patientID: Number(this.patientID()) }).subscribe({
                next: (data) => {
                    this.currentModel = data ?? null;
                    this.draftModel = null;
                    this.loading.set(false);
                },
                error: () => {
                    this.currentModel = null;
                    this.draftModel = null;
                    this.loading.set(false);
                }
            });
            return;
        }
        // Load current and draft in parallel
        this.staffApi.GetCurrentPatientBIPInfo({ patientID: Number(this.patientID()) }).subscribe({
            next: (data) => {
                this.currentModel = data ?? null;
            },
            error: () => {
                this.currentModel = null;
            }
        });

        this.staffApi.GetNewPatientBIPInfo({ patientID: Number(this.patientID()) }).subscribe({
            next: (data) => {
                this.draftModel = data ?? null;
                if (data==null || !this.draftModel?.CrisisCycle) {
                    if (!this.draftModel) {
                        this.draftModel = {};
                      }
                    this.draftModel.CrisisCycle = "<figure class=\"table\"><table class=\"ck-table-resized\" style=\"border-style:none;\"><colgroup><col style=\"width:33.33%;\"><col style=\"width:33.33%;\"><col style=\"width:33.34%;\"></colgroup><thead><tr><th style=\"background-color:#ea9999;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\" colspan=\"3\"><h2 style=\"text-align:center;\"><strong>Crisis Cycle&nbsp;</strong></h2></th></tr><tr><th style=\"border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\"><p style=\"text-align:center;\"><strong>Stage&nbsp;</strong></p></th><th style=\"border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\"><p style=\"text-align:center;\"><strong>What [client] looks like</strong></p></th><th style=\"border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\"><p style=\"text-align:center;\"><strong>Staff Response&nbsp;</strong></p></th></tr><tr><th style=\"background-color:#93c47d;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\"><strong>Baseline</strong></th><th style=\"background-color:#d9ead3;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\"><i><strong>[What does the client’s bx, emotions, etc. look like?]</strong></i></th><th style=\"background-color:#d9ead3;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\"><i><strong>[What should the staff be doing]</strong></i></th></tr><tr><th style=\"background-color:#ffd966;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\"><strong>Trigger</strong></th><th style=\"background-color:#fff2cc;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\">&nbsp;</th><th style=\"background-color:#fff2cc;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\">&nbsp;</th></tr><tr><th style=\"background-color:#f6b26b;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\"><strong>Escalation&nbsp;</strong></th><th style=\"background-color:#fce5cd;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\">&nbsp;</th><th style=\"background-color:#fce5cd;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\">&nbsp;</th></tr><tr><th style=\"background-color:#e06666;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\"><strong>Crisis</strong></th><th style=\"background-color:#f4cccc;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\">&nbsp;</th><th style=\"background-color:#f4cccc;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\">&nbsp;</th></tr></thead><tbody><tr><td style=\"background-color:#c27ba0;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\"><strong>Calming Down&nbsp;</strong></td><td style=\"background-color:#ead1dc;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\">&nbsp;</td><td style=\"background-color:#ead1dc;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\">&nbsp;</td></tr><tr><td style=\"background-color:#6d9eeb;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\"><strong>Recovery&nbsp;</strong></td><td style=\"background-color:#c9daf8;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\">&nbsp;</td><td style=\"background-color:#c9daf8;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\">&nbsp;</td></tr><tr><td style=\"background-color:#93c47d;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\"><strong>Back to Baseline</strong></td><td style=\"background-color:#d9ead3;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\">&nbsp;</td><td style=\"background-color:#d9ead3;border-color:#000000;border-width:1pt;padding:5pt;vertical-align:top;\">&nbsp;</td></tr></tbody></table></figure>";
                        
                }
                this.crisisCycle = this.draftModel?.CrisisCycle ?? null;
                this.initialCrisisCycle = this.crisisCycle ?? '';
                this.loading.set(false);
            },
            error: () => {
                this.draftModel = null;
                this.initialCrisisCycle = '';
                this.loading.set(false);
            }
        });
    }

    loadDataOffline() {
        this.loading.set(true);
        const data = this.offlineDataService.getOfflinePatientData(Number(this.patientID()));
        this.currentModel = data?.patientBipInfo ?? null;
        this.draftModel = null;
        this.crisisCycle = this.currentModel?.CrisisCycle ?? null;
        this.initialCrisisCycle = this.crisisCycle ?? '';
        this.loading.set(false);
    }

    getSaveObservable(reloadAfter: boolean = true) {
        if (!this.draftModel) return;
        this.draftModel.PatientID = Number(this.patientID());
        this.draftModel.CrisisCycle = this.crisisCycle ?? null;
        return this.staffApi.SavePatientBIPInfo({ body: this.draftModel }).pipe(
            tap((saved) => {
                this.draftModel = saved;
                if (reloadAfter) {
                    this.initialCrisisCycle = this.draftModel?.CrisisCycle ?? '';
                }
            })
        );
    }

    save() {
        if (!this.canEditBip) return;
        if (!this.draftModel) {
            // No draft BIP exists; cannot save without BIPID
            this.alerts.error('No draft BIP exists for this patient.');
            return;
        }
        this.getSaveObservable()?.subscribe({
            next: () => {
                this.alerts.success('Saved');
            },
            error: () => this.alerts.error('Failed to save')
        });
    }

    onCrisisCycleChange(event: any): void {
        if (event.editor) {
            const data = event.editor.getData();
            this.crisisCycle = data;
        }
    }

    onIsCurrentViewChange(event: any): void {
        if (!this.canEditBip) {
            this.isCurrentView = true;
            return;
        }
        const movingToCurrent = event?.value === true && this.isCurrentView === false;
        if (movingToCurrent && this.draftModel) {
            this.getSaveObservable(false)?.subscribe({ next: () => {}, error: () => {} });
        }
        if (this.isCurrentView) {
            this.initialCrisisCycle = this.crisisCycle ?? '';
        }
        this.isCurrentView = event.value;
    }

    ngOnDestroy(): void {
        // Final silent save when leaving page if in Draft
        if (!this.isCurrentView && this.draftModel) {
            this.getSaveObservable(false)?.subscribe({ next: () => {}, error: () => {} });
        }
        if (this.autoSaveHandle) {
            clearTimeout(this.autoSaveHandle);
            this.autoSaveHandle = null;
        }
    }

    @HostListener('window:beforeunload', ['$event'])
    handleBeforeUnload(event: Event) {
        if (!this.canEditBip) return;
        if (!this.isCurrentView && this.draftModel) {
            this.getSaveObservable(false)?.subscribe({ next: () => {}, error: () => {} });
        }
    }
}


